import { Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Wraps a single form field in the property edit modal with an optional
 * label, required marker and an admin-authored help popover.
 *
 * Respects the CMS admin's per-field visibility mode (`visible_in`):
 *   - "admin"  — only shown in the master admin dashboard
 *   - "user"   — only shown in the property-admin (user) dashboard
 *   - "hidden" — hidden everywhere
 *
 * Older settings docs only stored the boolean `visible`; falls back so those
 * keep rendering as `admin` (visible in the master admin dashboard).
 */
export default function CmsFieldWrap({
  fieldKey,
  label,
  required,
  children,
  className = "",
  settings,
  style,
  audience = "admin",              // "admin" | "user"
  formData,                        // optional — current form values, used to
                                    // detect + highlight a required-but-blank field
}) {
  const meta = (settings && settings[fieldKey]) || {};
  const visibleIn = (meta.visible_in
    || (meta.visible === false ? "hidden" : "admin"));

  // Field is hidden if the admin marked it hidden. Otherwise decide based on
  // which dashboard the wrap is rendering into:
  //   - "both"  → visible in both admin and user dashboards
  //   - "admin" → admin only
  //   - "user"  → user only
  if (visibleIn === "hidden") return null;
  if (audience === "admin" && !(visibleIn === "admin" || visibleIn === "both")) return null;
  if (audience === "user" && !(visibleIn === "user" || visibleIn === "both")) return null;

  // `required` prop is an "always required" override (used for the handful
  // of structurally-required fields like title/price); otherwise this is
  // driven by the admin-configurable "Required" tickbox on CMS → Fields.
  const isRequired = !!required || !!meta.required;
  const value = formData ? formData[fieldKey] : undefined;
  const isBlank = value === undefined || value === null || value === ""
    || (typeof value === "number" && Number.isNaN(value));
  const missing = isRequired && formData !== undefined && isBlank;

  return (
    <div
      className={`space-y-2 ${missing ? "ring-1 ring-red-400 bg-red-50/60 rounded p-2 -m-2" : ""} ${className}`}
      style={style}
      data-testid={missing ? `cms-field-missing-${fieldKey}` : undefined}
    >
      {label && (
        <div className="flex items-center gap-1.5">
          <Label>
            {label}
            {isRequired ? <span className="text-red-600 ml-0.5">*</span> : null}
          </Label>
          {meta.info && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                  title="Field guidance — click to view"
                  data-testid={`cms-field-info-btn-${fieldKey}`}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                className="max-w-xs text-xs leading-relaxed"
                data-testid={`cms-field-info-content-${fieldKey}`}
              >
                {meta.info}
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
      {children}
      {missing && (
        <p className="text-[11px] text-red-600" data-testid={`cms-field-required-warning-${fieldKey}`}>
          Required — please fill this in
        </p>
      )}
    </div>
  );
}
